import { _Product, useGlobalSettings } from "/global-stores";
import { CloseIcon } from "/icons";
import clsx from "clsx";
import { render } from "preact";
import { useCallback, useEffect } from "react";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

export type BackInStockModal = {
  open: boolean;
  loading: boolean;
  email: string;
  product?: _Product;
  variant_id?: number | null;
  success: boolean;
};

export const useBackInStockModal = create(
  immer<BackInStockModal>((set, get) => {
    /*Initial State*/

    return {
      open: false,
      loading: false,
      email: window?.customer?.email || "",
      product: null,
      variant_id: null,
      success: false,
    };
  })
);

function BackInStock() {
  const { open, email, variant_id, product, success } = useBackInStockModal();
  const { back_in_stock__api_endpoint, back_in_stock__api_method, back_in_stock__api_payload } =
    useGlobalSettings();

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      useBackInStockModal.setState({ success: false });
    }
  }, [open]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      useBackInStockModal.setState({ loading: true });
      const res = await fetch(back_in_stock__api_endpoint, {
        method: back_in_stock__api_method,
        headers: {
          "Content-Type": "application/json",
          revision: back_in_stock__api_endpoint.includes(
            "a.klaviyo.com/client/back-in-stock-subscriptions/"
          )
            ? "2023-10-15"
            : undefined,
        },
        body: back_in_stock__api_payload
          .replace(/\[email\]/gi, email)
          .replace(/\[variant_id\]/gi, `${variant_id}`),
      });

      if (res.status === 202) {
        useBackInStockModal.setState({ success: true, loading: false });
        return;
      }
      const data = await res.json();

      if (data?.errors?.[0]?.detail) {
        alert(data?.errors?.[0]?.detail);
      }

      useBackInStockModal.setState({ loading: false });
    },
    [
      back_in_stock__api_endpoint,
      back_in_stock__api_method,
      back_in_stock__api_payload,
      email,
      variant_id,
    ]
  );

  useEffect(() => {
    if (!open && variant_id) {
      setTimeout(() => {
        useBackInStockModal.setState({ variant_id: null });
      }, 150);
    }
  }, [open, variant_id]);

  return variant_id
    ? <div
        className={clsx("fixed inset-0 flex items-center justify-center", open ? "z-[100]" : "")}
      >
        <div
          className={clsx(
            "overlay font-group-1 fixed inset-0 bg-white/[0.72] transition-all",
            open ? "opacity-100" : "pointer-events-none opacity-0"
          )}
          onClick={() => useBackInStockModal.setState({ open: false })}
        />
        <form
          className={clsx(
            "relative grid w-full max-w-md gap-6 rounded-theme-md bg-white p-6 shadow-2xl transition-all",
            open ? "opacity-100" : "pointer-events-none opacity-0"
          )}
          onSubmit={handleSubmit}
        >
          <button
            className="absolute right-4 top-4 z-10 p-1 text-theme-text/80 hf:text-theme-text"
            type="button"
            onClick={() => useBackInStockModal.setState({ open: false })}
          >
            <CloseIcon className="h-5 w-5" />
          </button>
          <header className="">
            <h2 className="_h4">
              <strong>Notify me when available</strong>
            </h2>
            <p className="_p mt-1">
              Register your email address below to receive an email as soon as this product becomes
              available.
            </p>
          </header>
          <hr className="border-t border-t-gray-200" />
          {success
            ? <main className="grid gap-4">
                <p className="_p">
                  Thank you for your input. We will notify you when{" "}
                  <strong>{product?.title}</strong> is available again.
                </p>
                <button
                  className="button--md button-primary mt-6 w-full"
                  type="button"
                  onClick={() => useBackInStockModal.setState({ open: false })}
                >
                  Close
                </button>
              </main>
            : <main className="grid gap-4">
                <h3 className="p">
                  <strong>{product?.title}</strong>
                </h3>
                {product?.variants?.length > 1 && product?.variants?.[0]?.title !== "Default Title"
                  ? <label className="block w-full">
                      <span className="sr-only">Selected Variant</span>
                      <select
                        name="selected_variant"
                        className="input--primary--outline text-input--md w-full"
                        onChange={(e) =>
                          useBackInStockModal.setState({ variant_id: +e.currentTarget.value })
                        }
                      >
                        {product?.variants?.map((variant) => (
                          <option key={variant.id} value={variant.id} disabled={variant.available}>
                            {variant.title}
                          </option>
                        ))}
                      </select>
                    </label>
                  : null}
                <label>
                  <span className="sr-only">Email</span>
                  <input
                    className="input--primary--outline text-input--md w-full"
                    name="email"
                    placeholder="Enter your email"
                    type="text"
                    autoComplete="email"
                    autoCorrect="off"
                    value={email}
                    onChange={(e) => useBackInStockModal.setState({ email: e.currentTarget.value })}
                  />
                </label>
                <button className="button--md button-primary mt-6 w-full" type="submit">
                  Notify me when available
                </button>
                <p className="_p">
                  <small>We'll notify you when this product is arrives.</small>
                </p>
              </main>}
        </form>
      </div>
    : null;
}

export const initBackInStock = () => {
  const root = document.createElement("div");
  document.body.append(root);

  render(<BackInStock />, root);
};
